/**
 * Hook for using cached technique images
 * 
 * Provides cached image URLs for technique images when available,
 * falling back to original URLs when not cached or online.
 */

import { useState, useEffect } from 'react';
import { techniqueImageManager } from '@/services/offline/assets/TechniqueImageManager';
import { useNetworkStatus } from '@/contexts/offline/hooks/useOfflineState';

interface UseCachedTechniqueImageResult {
  imageUrl: string | null;
  isLoading: boolean;
  isCached: boolean;
}

export function useCachedTechniqueImage(originalUrl?: string): UseCachedTechniqueImageResult {
  const [imageUrl, setImageUrl] = useState<string | null>(originalUrl || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCached, setIsCached] = useState(false);
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    if (!originalUrl) {
      setImageUrl(null);
      setIsCached(false);
      setIsLoading(false);
      return;
    }

    // If online, use original URL
    if (isOnline) {
      setImageUrl(originalUrl);
      setIsCached(false);
      setIsLoading(false);
      return;
    }

    // If offline, try to get cached version
    const loadCachedImage = async () => {
      setIsLoading(true);
      
      try {
        const cachedUrl = await techniqueImageManager.getCachedImageUrl(originalUrl);
        
        if (cachedUrl) {
          setImageUrl(cachedUrl);
          setIsCached(true);
        } else {
          // Fallback to original URL (will likely fail to load offline, but that's expected)
          setImageUrl(originalUrl);
          setIsCached(false);
        }
      } catch (error) {
        console.error('Failed to load cached image:', error);
        setImageUrl(originalUrl);
        setIsCached(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadCachedImage();
  }, [originalUrl, isOnline]);

  return {
    imageUrl,
    isLoading,
    isCached
  };
}
